import React from 'react';
import { useSupabaseClient } from '../utils/supabase';
import { AlertTriangle, Trash2, Edit, Plus, X, Bell, MessageSquare, Layout, Copy, Check } from 'lucide-react';

// Type definitions
type DisplayType = 'toast' | 'card' | 'modal';
type Position = 'bottom-right' | 'top-right' | 'center';
type Behavior = 'timeout' | 'first-to-accept' | 'persistent';

interface AlertType {
  id: string;
  name: string;
  description: string;
  display_type: DisplayType;
  position: Position;
  behavior: Behavior;
  timeout_duration?: number;
  design_config: {
    backgroundColor: string;
    textColor: string;
    accentColor: string;
    icon: string;
    borderRadius: string;
  };
  endpoint_url: string;
  user_id: string;
  call_count: number;
}

function AlertTypes() {
  const [alertTypes, setAlertTypes] = React.useState<AlertType[]>([]);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [newAlertType, setNewAlertType] = React.useState({
    name: '',
    description: '',
    display_type: 'toast' as DisplayType,
    position: 'bottom-right' as Position,
    behavior: 'persistent' as Behavior,
    timeout_duration: 5,
    design_config: {
      backgroundColor: '#ffffff',
      textColor: '#000000',
      accentColor: '#4f46e5',
      icon: 'bell',
      borderRadius: '0.5rem'
    }
  });

  const supabase = useSupabaseClient();
  const baseWebhookUrl = `${window.location.protocol}//${window.location.host}/api/webhooks`;

  React.useEffect(() => {
    fetchAlertTypes();
  }, []);

  const fetchAlertTypes = async () => {
    const { data, error } = await supabase
      .from('alert_types')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setAlertTypes(data);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    const { error } = await supabase
      .from('alert_types')
      .insert([{
        ...newAlertType,
        user_id: user.id
      }]);

    if (!error) {
      setShowAddModal(false);
      setNewAlertType({
        name: '',
        description: '',
        display_type: 'toast',
        position: 'bottom-right',
        behavior: 'persistent',
        timeout_duration: 5,
        design_config: {
          backgroundColor: '#ffffff',
          textColor: '#000000',
          accentColor: '#4f46e5',
          icon: 'bell',
          borderRadius: '0.5rem'
        }
      });
      fetchAlertTypes();
    } else {
      console.error('Error creating alert type:', error);
    }
  };

  const getDisplayIcon = (type: DisplayType) => {
    switch (type) {
      case 'toast':
        return <Bell className="h-5 w-5" />;
      case 'card':
        return <MessageSquare className="h-5 w-5" />;
      case 'modal':
        return <Layout className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getWebhookUrl = (endpointUrl: string) => {
    return `${baseWebhookUrl}/${endpointUrl}`;
  };

  const handleCopyUrl = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Alert Types</h1>
          <p className="mt-2 text-sm text-gray-700">
            Configure different types of alerts and their behaviors.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Alert Type
          </button>
        </div>
      </div>
      
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Behavior
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Endpoint URL
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Call Count
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {alertTypes.map((alertType) => (
                    <tr key={alertType.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        <div className="flex items-center">
                          {getDisplayIcon(alertType.display_type)}
                          <span className="ml-2">{alertType.name}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {alertType.display_type} ({alertType.position})
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {alertType.behavior}
                        {alertType.behavior === 'timeout' && ` (${alertType.timeout_duration}s)`}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                        <div className="flex items-center space-x-2">
                          <div className="max-w-md truncate" title={getWebhookUrl(alertType.endpoint_url)}>
                            {getWebhookUrl(alertType.endpoint_url)}
                          </div>
                          <button
                            onClick={() => handleCopyUrl(alertType.id, getWebhookUrl(alertType.endpoint_url))}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy URL"
                          >
                            {copiedId === alertType.id ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {alertType.call_count || 0}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <button className="text-indigo-600 hover:text-indigo-900 mr-4">
                          <Edit className="h-5 w-5" />
                        </button>
                        <button className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Alert Type Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity">
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Information</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          required
                          value={newAlertType.name}
                          onChange={(e) => setNewAlertType({ ...newAlertType, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          value={newAlertType.description}
                          onChange={(e) => setNewAlertType({ ...newAlertType, description: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Display Settings */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Display Settings</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="display_type" className="block text-sm font-medium text-gray-700">
                          Display Type
                        </label>
                        <select
                          id="display_type"
                          name="display_type"
                          value={newAlertType.display_type}
                          onChange={(e) => setNewAlertType({ ...newAlertType, display_type: e.target.value as DisplayType })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="toast">Toast</option>
                          <option value="card">Card</option>
                          <option value="modal">Modal</option>
                        </select>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                          Position
                        </label>
                        <select
                          id="position"
                          name="position"
                          value={newAlertType.position}
                          onChange={(e) => setNewAlertType({ ...newAlertType, position: e.target.value as Position })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="bottom-right">Bottom Right</option>
                          <option value="top-right">Top Right</option>
                          <option value="center">Center</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Behavior Settings */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Behavior Settings</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="behavior" className="block text-sm font-medium text-gray-700">
                          Behavior
                        </label>
                        <select
                          id="behavior"
                          name="behavior"
                          value={newAlertType.behavior}
                          onChange={(e) => setNewAlertType({ ...newAlertType, behavior: e.target.value as Behavior })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="timeout">Timeout</option>
                          <option value="first-to-accept">First to Accept</option>
                          <option value="persistent">Persistent</option>
                        </select>
                      </div>

                      {newAlertType.behavior === 'timeout' && (
                        <div className="sm:col-span-3">
                          <label htmlFor="timeout_duration" className="block text-sm font-medium text-gray-700">
                            Timeout Duration (seconds)
                          </label>
                          <input
                            type="number"
                            id="timeout_duration"
                            name="timeout_duration"
                            min="1"
                            value={newAlertType.timeout_duration}
                            onChange={(e) => setNewAlertType({ ...newAlertType, timeout_duration: parseInt(e.target.value) })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Design Settings */}
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Design Settings</h3>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <label htmlFor="backgroundColor" className="block text-sm font-medium text-gray-700">
                          Background Color
                        </label>
                        <input
                          type="color"
                          id="backgroundColor"
                          name="backgroundColor"
                          value={newAlertType.design_config.backgroundColor}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, backgroundColor: e.target.value }
                          })}
                          className="mt-1 block w-full h-8 rounded-md border-gray-300"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="textColor" className="block text-sm font-medium text-gray-700">
                          Text Color
                        </label>
                        <input
                          type="color"
                          id="textColor"
                          name="textColor"
                          value={newAlertType.design_config.textColor}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, textColor: e.target.value }
                          })}
                          className="mt-1 block w-full h-8 rounded-md border-gray-300"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label htmlFor="accentColor" className="block text-sm font-medium text-gray-700">
                          Accent Color
                        </label>
                        <input
                          type="color"
                          id="accentColor"
                          name="accentColor"
                          value={newAlertType.design_config.accentColor}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, accentColor: e.target.value }
                          })}
                          className="mt-1 block w-full h-8 rounded-md border-gray-300"
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="icon" className="block text-sm font-medium text-gray-700">
                          Icon
                        </label>
                        <select
                          id="icon"
                          name="icon"
                          value={newAlertType.design_config.icon}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, icon: e.target.value }
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="bell">Bell</option>
                          <option value="alert-triangle">Alert Triangle</option>
                          <option value="info">Info</option>
                          <option value="check-circle">Check Circle</option>
                        </select>
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="borderRadius" className="block text-sm font-medium text-gray-700">
                          Border Radius
                        </label>
                        <select
                          id="borderRadius"
                          name="borderRadius"
                          value={newAlertType.design_config.borderRadius}
                          onChange={(e) => setNewAlertType({
                            ...newAlertType,
                            design_config: { ...newAlertType.design_config, borderRadius: e.target.value }
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="0">None</option>
                          <option value="0.25rem">Small</option>
                          <option value="0.5rem">Medium</option>
                          <option value="1rem">Large</option>
                          <option value="9999px">Full</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                    >
                      Create
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlertTypes;